import { atom } from 'nanostores'
import { $cart } from '~/store/cart'
import { MANUAL_CART_EVENTS } from '@/configs/settings'
import { DEFAULT_WATING_TIME } from '~/configs/constants'
import { BaseCart } from './base-cart'
export class ManualCart extends BaseCart {
    constructor($cart, $products) {
        super()
        this.$products = $products
        this.$cart = $cart
        this.cartId = undefined
        this.currency = undefined
        this.eventManager = undefined
        this.videoId = undefined
        this.playlistId = undefined
        this.formattedProducts = []
        this.infoInternal = {}
    }

    get products() {
        return this.$products.get()
    }

    setBaseInfo(data, videoId, playlistId = '') {
        this.videoId = data.videoId
        this.playlistId = data.playlistId
        this.eventManager = data.eventManager
        let internal = {
            videoId: videoId,
            playlistId: playlistId,
            eventManager: data.eventManager,
        }
        this.infoInternal[`${videoId}_${playlistId}`] = internal
    }

    syncCartState(videoId, playlistId = '') {
        return this.getCartState(videoId, playlistId).then((cartState) => {
            this.syncCartProperties(cartState)
            this.$cart.set(cartState)
        })
    }

    getCartId() {
        return this.cartId
    }

    getCurrency() {
        return this.currency
    }

    getCurrentEventManager(videoId, playlistId = '') {
        if (this.infoInternal?.[`${videoId}_${playlistId}`]) {
            return this.infoInternal?.[`${videoId}_${playlistId}`].eventManager
        }
        return this.eventManager
    }

    async getCartState(videoId, playlistId = '') {
        // Create a promise that resolves when the syncCartStatePromise is called.
        const syncCartStatePromise = new Promise((resolve, reject) => {
            const currentEventManager = this.getCurrentEventManager(videoId, playlistId)
            currentEventManager.emit(
                MANUAL_CART_EVENTS.SYNC_CART_STATE,
                {
                    playlistId: this.playlistId,
                    videoId: this.videoId,
                },
                (data) => {
                    this.currency = data.currency
                    this.cartId = data?.cartId?.toString()
                    const currentProducts = this.$products.get()
                    const cartItemsInProducts = (data?.items || []).filter((cartItem) =>
                        currentProducts.some((product) => cartItem.productSku === product.productSku)
                    )
                    const manualCartData = (cartItemsInProducts || []).map((item) => ({
                        id: item.id.toString(),
                        productSku: item.productSku,
                        name: item.name,
                        image: item.image,
                        price: item.price,
                        description: item.description,
                        quantity: item.quantity,
                        variantTitle: item.variantTitle,
                        properties: this.getCartProperties(item.id.toString()),
                    }))
                    resolve(manualCartData)
                }
            )
        })

        // Wait for the syncCartStatePromise to resolve and return its result.
        return await syncCartStatePromise
    }

    async syncProducts(loraProducts, videoId, playlistId = '') {
        // Extract an array of SKUs from the products array
        this.productSkus = loraProducts.map((product) => product.productSku).filter((sku) => sku !== null)
        const currentEventManager = this.getCurrentEventManager(videoId, playlistId)
        this.infoInternal[`${videoId}_${playlistId}`].timeoutId = setTimeout(() => {
            if (this.infoInternal[`${videoId}_${playlistId}`].isSynced) {
                return
            }
            currentEventManager.emit(MANUAL_CART_EVENTS.SYNCED_PRODUCT, loraProducts)
        }, DEFAULT_WATING_TIME)
        currentEventManager.emit(
            MANUAL_CART_EVENTS.SYNC_PRODUCT,
            {
                playlistId: this.playlistId,
                videoId: this.videoId,
                productSkus: this.productSkus,
            },
            async (manualProducts) => {
                const currentProducts = this.$products.get()
                const unsyncedProducts = loraProducts.filter(
                    (product) =>
                        !!product.productSku &&
                        !!product.productLink &&
                        !currentProducts.some((item) => item.productSku === product.productSku)
                )

                const newProducts = unsyncedProducts
                    .map((product) =>
                        this.formatStandardProduct(manualProducts, product.productSku, {
                            cmsProductId: product.id,
                        })
                    )
                    .filter((newProduct) => !!newProduct)

                const totalProducts = [...currentProducts, ...newProducts]
                this.$products.set(totalProducts)

                // we only return products of the argument
                this.formattedProducts = loraProducts.map((product) => {
                    const newProduct = totalProducts.find((item) => item.productSku === product.productSku)
                    if (newProduct) return { ...product, ...newProduct, isUsingCartIntegrated: true }
                    return product
                })
                const currentEventManager = this.getCurrentEventManager(videoId, playlistId)
                currentEventManager.emit(MANUAL_CART_EVENTS.SYNCED_PRODUCT, this.formattedProducts)
                this.infoInternal[`${videoId}_${playlistId}`].isSynced = true
                clearTimeout(this.infoInternal[`${videoId}_${playlistId}`].timeoutId)
            }
        )

        return loraProducts
    }

    formatStandardProduct(manualProducts, targetSku, additionalData = {}) {
        // Find the product by SKU
        const manualProduct = manualProducts.find((product) => product.productSku === targetSku)

        if (!manualProduct) return null

        return {
            isSynced: true,
            hasVariants: manualProduct.options?.length > 0,
            productSku: manualProduct.productSku,
            image: manualProduct.image,
            name: manualProduct.name,
            description: manualProduct.description,
            options: manualProduct.options || [],
            variants: manualProduct.variants.map((variation) => ({
                id: variation.id.toString(),
                name: variation.name,
                promotionPrice: variation.promotionPrice || null,
                price: variation.price,
                options: variation.options || [],
                images: variation.images || [],
            })),
            properties: manualProduct.properties,
            ...additionalData,
        }
    }

    async waitForPromiseWithTimeout(promise, timeout = DEFAULT_WATING_TIME) {
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                reject(new Error('Promise not resolved within the specified time.'))
            }, timeout)

            promise
                .then((result) => {
                    clearTimeout(timer)
                    resolve(result)
                })
                .catch((error) => {
                    clearTimeout(timer)
                    reject(error)
                })
        })
    }

    async addToCart(itemId, quantity = 1, properties = {}, videoId, playlistId = '') {
        // Create a promise that resolves when the addToCartCallback is called.
        const addToCartPromise = new Promise(async (resolve, reject) => {
            if (!this.cartId) {
                await this.syncCartState(videoId, playlistId)
            }
            const currentEventManager = this.getCurrentEventManager(videoId, playlistId)
            currentEventManager.emit(
                MANUAL_CART_EVENTS.ADD_TO_CART,
                {
                    cartId: this.cartId,
                    playlistId: playlistId,
                    videoId: videoId,
                    itemId: itemId,
                    quantity: quantity,
                    properties: properties,
                },
                ({ success, message }) => {
                    this.persistCartProperties(itemId, properties)
                    resolve({ success, message })
                }
            )
        })
        // Use the withTimeout helper function to handle timeouts and errors
        try {
            // Use the helper function to wait for addToCartPromise and clear the timeout
            return await this.waitForPromiseWithTimeout(addToCartPromise)
        } catch (error) {
            // Handle errors from addToCartPromise and timeouts
            console.error('Error:', error.message)
            throw error // Rethrow the error if needed
        }
    }

    async updateItemInCart(payload, videoId, playlistId = '') {
        // Create a promise that resolves when the updateItemInCartPromise is called.
        const updateItemInCartPromise = new Promise((resolve, reject) => {
            const currentEventManager = this.getCurrentEventManager(videoId, playlistId)
            currentEventManager.emit(
                MANUAL_CART_EVENTS.UPDATE_ITEM_IN_CART,
                {
                    cartId: this.cartId,
                    playlistId: playlistId,
                    videoId: videoId,
                    itemId: payload.id,
                    quantity: payload.quantity,
                    properties: payload.properties,
                },
                (res) => {
                    if (payload.quantity === 0) this.deleteCartProperties(payload.id)
                    else this.persistCartProperties(payload.id, payload.properties)
                    resolve(res)
                }
            )
        })

        // Use the withTimeout helper function to handle timeouts and errors
        try {
            // Use the helper function to wait for addToCartPromise and clear the timeout
            return await this.waitForPromiseWithTimeout(updateItemInCartPromise)
        } catch (error) {
            // Handle errors from addToCartPromise and timeouts
            console.error('Error:', error.message)
            throw error // Rethrow the error if needed
        }
    }

    async goCheckout(videoId, playlistId = '') {
        // Create a promise that resolves when the checkoutPromise is called.
        const checkoutPromise = new Promise((resolve, reject) => {
            const currentEventManager = this.getCurrentEventManager(videoId, playlistId)
            currentEventManager.emit(
                MANUAL_CART_EVENTS.CHECKOUT,
                {
                    playlistId: this.playlistId,
                    videoId: this.videoId,
                },
                (res) => {
                    resolve(res)
                }
            )
        })

        // Use the withTimeout helper function to handle timeouts and errors
        try {
            // Use the helper function to wait for addToCartPromise and clear the timeout
            return await this.waitForPromiseWithTimeout(checkoutPromise)
        } catch (error) {
            // Handle errors from addToCartPromise and timeouts
            console.error('Error:', error.message)
            throw error // Rethrow the error if needed
        }
    }
}

const $products = atom([])

const manualCart = new ManualCart($cart, $products)

export default manualCart
